/**
 * Import d'un export **Password Depot** XML (AceBIT, « Fichier > Exporter >
 * Fichier XML » : ses utilisateurs doivent pouvoir partir avec TOUT leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le document `<passwordfile>` porte un `header` (dont
 * `encrypted`) et une arborescence `passwords > group[name] > item | group`.
 * Chaque `item` a son genre (`type` : 0 mot de passe, 1 carte, 2 licence,
 * 3 identite, 4 information, 5 banque, 8-10 bureau a distance, etc.), ses
 * elements (`description` = nom, `username`, `password`, `url`, `comment`,
 * `category`) et ses `customfields > field` (`name`, `value`, `visible`) : les
 * champs `IDS_*` des cartes, identites, licences et comptes bancaires sont
 * ranges dans les champs types du coffre, tout autre champ devient un champ
 * personnalise, masque si l'export le cache ou si son libelle designe un secret.
 * Les favoris (`favorites > favorite`, empreinte de l'item) sont repris ; la
 * corbeille (`recyclebin`) ne l'est pas : ce sont des entrees supprimees.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import type { VaultItemType } from '@klavox/vault-model';
import { csvColumnField, joinNotes, type ImportedEntry } from './csv-import.js';
import { normalizeTags, parseTagList } from './entry-tags.js';
import { childNamed, childrenNamed, parseXml, type XmlNode } from './kdbx-import.js';
import { normalizeDocumentDate, normalizeExpiry, normalizeTypedFields, serverHost, typedFieldGroup } from './typed-fields.js';

/** Genres d'item Password Depot (`type`) -> type d'entree du coffre. */
const DEPOT_TYPES: Readonly<Record<string, VaultItemType>> = {
  '0': 'login',
  '1': 'card',
  '2': 'software-license',
  '3': 'identity',
  '4': 'note',
  '5': 'bank',
  '6': 'note',
  '7': 'note',
  '8': 'login',
  '9': 'login',
  '10': 'login',
  '11': 'note',
};

/** Connexions bureau a distance (RDP, PuTTY, TeamViewer) : leur `url` est un hote, jamais une adresse web. */
const REMOTE_TYPES: Readonly<Record<string, string>> = { '8': 'Hote (RDP)', '9': 'Hote (PuTTY)', '10': 'ID TeamViewer' };

/** Champs `IDS_*` reconnus, par genre -> cle de champ type (`typed-fields.ts`). */
const DEPOT_TYPED_FIELDS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  card: {
    IDS_CardHolder: 'cardholder',
    IDS_CardNumber: 'number',
    IDS_CardExpires: 'expiry',
    IDS_CardCode: 'cvv',
    IDS_CardPIN: 'pin',
    IDS_CardBank: 'bank',
  },
  identity: {
    IDS_IdentityFirstName: 'firstName',
    IDS_IdentityLastName: 'lastName',
    IDS_IdentityEmail: 'email',
    IDS_IdentityPhone: 'phone',
    IDS_IdentityCompany: 'company',
    IDS_IdentityCity: 'city',
    IDS_IdentityState: 'region',
    IDS_IdentityZIP: 'postalCode',
    IDS_IdentityCountry: 'country',
    IDS_IdentityBirthDate: 'birthDate',
  },
  'software-license': {
    IDS_LicenseVersion: 'productVersion',
    IDS_LicenseName: 'licensedTo',
    IDS_LicenseKey: 'licenseKey',
    IDS_LicenseURL: 'downloadUrl',
    IDS_LicenseEmail: 'registeredEmail',
    IDS_LicensePurchaseDate: 'purchaseDate',
    IDS_LicenseOrderNumber: 'orderNumber',
    IDS_LicenseExpires: 'softwareExpiry',
  },
  bank: {
    IDS_BankName: 'bankName',
    IDS_BankAccountHolder: 'accountHolder',
    IDS_BankAccountNumber: 'accountNumber',
    IDS_BankIBAN: 'iban',
    IDS_BankBIC: 'bic',
    IDS_BankSWIFT: 'bic',
    IDS_BankCode: 'routingNumber',
    IDS_BankPIN: 'bankPin',
  },
};

/** Lignes d'adresse d'une identite, reunies dans le champ `address`. */
const ADDRESS_LINES = ['IDS_IdentityAddress1', 'IDS_IdentityHouseNumber', 'IDS_IdentityAddress2'];

/** Elements d'item repris ailleurs, ou purement techniques (icone, importance, horodatages). */
const KNOWN_ELEMENT =
  /^(?:description|type|password|username|url|comment|category|tags|customfields|fingerprint|expirydate|importance|template|imageindex|imagecustom|usagecount|autocompletemethod|warnmsg|warnings)$|(?:date|time|modified|accessed|created)$/iu;

/**
 * Libelle d'un champ `IDS_*` non repris qui designe un secret : numero, code,
 * cle. Plus large que `SECRET_COLUMN` (un `IDS_BankAccountNumber` inattendu
 * reste masque) ; ne vise que les champs d'origine Password Depot.
 */
const DEPOT_SECRET_FIELD = /pass|pin|code|key|number|iban|tan$|secret|cvv|cvc/iu;

/** Date « zero » Delphi : Password Depot l'ecrit pour une date jamais renseignee. */
const DELPHI_EPOCH = /^30[./-]12[./-]1899/u;

function childText(node: XmlNode | null, tag: string): string {
  return childNamed(node, tag)?.text ?? '';
}

/** Le document Password Depot, ou `null` si le texte n'en est pas un. */
function depotDocument(text: string): XmlNode | null {
  if (!/^[\s\uFEFF]*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<passwordfile[\s>]/u.test(text)) return null;
  let document: XmlNode;
  try {
    document = parseXml(text);
  } catch {
    return null;
  }
  const file = childNamed(document, 'passwordfile');
  return childNamed(file, 'passwords') !== null ? file : null;
}

function withFields(customFields: readonly EntryCustomField[]): { customFields?: readonly EntryCustomField[] } {
  return customFields.length > 0 ? { customFields } : {};
}

/** Horodatage Password Depot `JJ.MM.AAAA hh:mm:ss` (attribut `fmt`, Windows comme macOS). */
const DEPOT_DATETIME = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/u;

/**
 * Date du mot de passe : Password Depot ne date que l'item ; `lastmodified`
 * (derniere modification) = borne haute, jamais `created` ni `lastaccessed`.
 * Heure locale sans fuseau, lue en UTC : juste a quelques heures pres. Aucune
 * date sans mot de passe ; date zero Delphi, absente ou illisible : l'import
 * vaut pose.
 */
function depotPasswordChangedAt(item: XmlNode, password: string): { passwordChangedAt?: number } {
  const match = password.length > 0 ? DEPOT_DATETIME.exec(childText(item, 'lastmodified').trim()) : null;
  if (match === null) return {};
  const [day = 0, month = 0, year = 0, hour = 0, minute = 0, second = 0] = match.slice(1).map((part) => Number(part ?? 0));
  const at = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  // Un jour ou un mois hors bornes (31.02, 13e mois) deborde sur la date suivante : illisible.
  const exact = at.getUTCDate() === day && at.getUTCMonth() === month - 1;
  return exact && at.getTime() > 0 ? { passwordChangedAt: at.getTime() } : {};
}

/** Une entree a partir d'un `item`, rangee dans `folderPath`. */
function depotEntry(item: XmlNode, folderPath: readonly string[], favorites: ReadonlySet<string>): ImportedEntry {
  const kind = childText(item, 'type').trim();
  const type: VaultItemType = DEPOT_TYPES[kind] ?? 'login';
  const group = typedFieldGroup(type);
  const typedNames = group !== null ? (DEPOT_TYPED_FIELDS[group] ?? {}) : {};
  const remoteLabel = REMOTE_TYPES[kind];
  const typed: Record<string, string> = {};
  const customFields: EntryCustomField[] = [];
  const notes: string[] = [childText(item, 'comment')];
  const addressLines: Record<string, string> = {};

  for (const field of childrenNamed(childNamed(item, 'customfields'), 'field')) {
    const name = childText(field, 'name').trim();
    const value = childText(field, 'value');
    if (value.trim().length === 0) continue;
    const key = typedNames[name];
    if (key !== undefined && typed[key] === undefined) {
      typed[key] = value.trim();
      continue;
    }
    if (type === 'identity' && ADDRESS_LINES.includes(name)) {
      addressLines[name] = value.trim();
      continue;
    }
    if (name === 'IDS_InformationText') {
      notes.push(value);
      continue;
    }
    const label = name.replace(/^IDS_/u, '') || 'Champ';
    const hidden = childText(field, 'visible').trim() === '0' || (name.startsWith('IDS_') && DEPOT_SECRET_FIELD.test(name));
    customFields.push(hidden ? { name: label, value: value.trim(), hidden: true } : csvColumnField(label, value.trim()));
  }
  if (type === 'identity') {
    const address = ADDRESS_LINES.map((line) => addressLines[line] ?? '').filter((line) => line.length > 0);
    if (address.length > 0) typed['address'] = address.join(' ');
    // Password Depot ecrit les dates en `JJ.MM.AAAA` ; le coffre les range en `AAAA-MM-JJ`.
    if (typed['birthDate'] !== undefined) typed['birthDate'] = normalizeDocumentDate(typed['birthDate']) ?? typed['birthDate'];
  }
  if (type === 'card' && typed['expiry'] !== undefined && normalizeExpiry(typed['expiry']) === null) {
    // Expiration illisible : gardee en champ plutot que perdue ou mal lue.
    customFields.push({ name: 'Expiration', value: typed['expiry'], hidden: false });
    delete typed['expiry'];
  }

  const rawUrl = childText(item, 'url').trim();
  const isLogin = type === 'login';
  const username = childText(item, 'username').trim();
  // Jamais rogne : une espace peut faire partie du mot de passe.
  const password = childText(item, 'password');
  if (remoteLabel !== undefined && rawUrl.length > 0) customFields.unshift({ name: remoteLabel, value: rawUrl, hidden: false });
  if (!isLogin) {
    // Hors identifiant, l'identifiant et le mot de passe d'un item restent en champs, jamais perdus.
    if (username.length > 0) customFields.unshift({ name: 'Identifiant', value: username, hidden: false });
    if (password.length > 0) customFields.unshift({ name: 'Mot de passe', value: password, hidden: true });
    if (rawUrl.length > 0) customFields.push({ name: 'Adresse', value: rawUrl, hidden: false });
  }
  const expiry = childText(item, 'expirydate').trim();
  if (expiry.length > 0 && !DELPHI_EPOCH.test(expiry)) customFields.push({ name: 'Expiration du mot de passe', value: expiry, hidden: false });
  for (const child of item.children) {
    const value = child.text.trim();
    if (KNOWN_ELEMENT.test(child.tag) || child.children.length > 0 || value.length === 0) continue;
    customFields.push(csvColumnField(child.tag, value));
  }

  const uris = isLogin && remoteLabel === undefined && rawUrl.length > 0 ? [rawUrl] : [];
  const fields = group !== null ? normalizeTypedFields(group, typed) : {};
  const tags = normalizeTags([...parseTagList(childText(item, 'category')), ...parseTagList(childText(item, 'tags'))]);
  const fingerprint = childText(item, 'fingerprint').trim();
  const host = uris.length > 0 ? serverHost(rawUrl).replace(/^www\./u, '') : '';
  const name = childText(item, 'description').trim() || host || rawUrl || (isLogin ? username : '') || fields['cardholder'] || 'Sans nom';
  return {
    type,
    name,
    username: isLogin ? username : '',
    password: isLogin ? password : '',
    ...depotPasswordChangedAt(item, isLogin ? password : ''),
    uris,
    notes: joinNotes(notes),
    totpSecret: '',
    favorite: fingerprint.length > 0 && favorites.has(fingerprint),
    ...withFields(customFields),
    ...(group !== null && Object.keys(fields).length > 0 ? { fields } : {}),
    ...(tags.length > 0 ? { tags } : {}),
    ...(folderPath.length > 0 ? { folderPath } : {}),
  };
}

/** Un export Password Depot se reconnait a son document `<passwordfile>` portant `passwords`. */
export function isPasswordDepotXml(text: string): boolean {
  return depotDocument(text) !== null;
}

/**
 * Parse un export Password Depot XML : chaque item a son genre, rattache au
 * chemin de ses groupes.
 *
 * @throws {Error} export chiffre (`header > encrypted` a `True`) : rien n'est lisible
 */
export function parsePasswordDepotXml(text: string): ImportedEntry[] {
  const file = depotDocument(text);
  if (file === null) throw new Error('export Password Depot : structure inattendue');
  if (childText(childNamed(file, 'header'), 'encrypted').trim().toLowerCase() === 'true') {
    throw new Error('export Password Depot chiffre : exportez a nouveau en XML sans chiffrement, puis supprimez le fichier apres import');
  }
  const favorites = new Set(
    childrenNamed(childNamed(file, 'favorites'), 'favorite')
      .map((favorite) => (favorite.attributes['fingerprint'] ?? favorite.text).trim())
      .filter((fingerprint) => fingerprint.length > 0),
  );
  const entries: ImportedEntry[] = [];
  const visit = (node: XmlNode, path: readonly string[]): void => {
    for (const child of node.children) {
      if (child.tag === 'item') {
        entries.push(depotEntry(child, path, favorites));
      } else if (child.tag === 'group') {
        const name = (child.attributes['name'] ?? '').trim();
        visit(child, name.length > 0 ? [...path, name] : path);
      }
    }
  };
  visit(childNamed(file, 'passwords')!, []);
  return entries;
}
